import { useOrgConfig } from "./Hooks/OrgConfigHooks";
import { useIntentsColumnPrefs } from "./Hooks/UiPrefsHooks";
import { useEffect, useRef, useState } from 'react';
import usecustomStyles from "./customStyles";
import MoneyDisplay from "./MoneyDisplay";
import WarnTriangle from "./WarnTriangle";
import BoolDisplay from "./BoolDisplay";
import InlineEditCell from "./InlineEditCell";
import SplitUpdateConfirmModal from './SplitUpdateConfirmModal';
import WizardUpdateConfirmModal from './WizardUpdateConfirmModal';
import BudgetSyncModal from './BudgetSyncModal';
import {
  buildSavePayload, checkBudgetSync, confirmStepFor, defaultEditDimension,
  inlineEditKind, inlineFormValues, submitIntentSave,
} from './intentSave';
import {intentTypeIcons, repeatableTooltipedIcon, customStyle, darkthemecolors} from "../config.js";
import { useSelector } from "react-redux";
import utils from './utils';
import { useTheme }  from '../Common/ThemeContext';
import { APIClient } from "../helpers/api_helper";
import { Pagination } from 'antd';
import {
    Alert,
    Table,
    Typography,
    Tag,
    Button,
    Input,
    Modal,
    Popconfirm,
    Space,
    Tooltip
  } from "antd";

import {BranchesOutlined, EditFilled, EditOutlined, EyeInvisibleOutlined, EyeOutlined,
        FlagFilled, FlagOutlined, LeftOutlined, LoadingOutlined, RightOutlined, SettingOutlined} from '@ant-design/icons';

const api = new APIClient();
const { TextArea } = Input;

const { Text, Link } = Typography;
const customStyles = usecustomStyles();

const OverrideIcon = ({ field, intent }) =>
  intent.manually_overridden_fields?.includes(field) ? (
    <Tooltip title="Vrednost je bila ročno spremenjena.">
      <EditOutlined style={{ color: '#1677ff', marginLeft: 4 }} />
    </Tooltip>
  ) : null;


/** The column-layout control: a gear outside edit mode, reset/done inside it.
 *
 * Exported so a page can place it among its own action buttons (the transactions
 * list puts it in the card header) instead of letting IntentsTable render it
 * above the table. Pair it with IntentsTable's `columnEditMode` /
 * `onColumnEditModeChange` props so the two stay on the same state.
 */
export const IntentsColumnConfig = ({ editMode, setEditMode }) => {
  const { resetColumns } = useIntentsColumnPrefs();

  if (editMode) {
    return (
      <Space size={4}>
        <Popconfirm
          title="Ponastavi stolpce na privzete?"
          okText="Ponastavi"
          cancelText="Prekliči"
          onConfirm={resetColumns}
        >
          <Button size="small">Ponastavi</Button>
        </Popconfirm>
        <Button size="small" type="primary" onClick={() => setEditMode(false)}>
          Končaj urejanje
        </Button>
      </Space>
    );
  }

  return (
    <Tooltip title="Uredi stolpce">
      <Button size="small" type="text" icon={<SettingOutlined />}
              onClick={() => setEditMode(true)} />
    </Tooltip>
  );
};

const IntentsTable = (props) => {

  const { theme } = useTheme();

  // Status-based cell background colors (invoice/payment), per customer color coding:
  //  - is_issued true → green, false → red (invoice cell + its type-relevant date)
  //  - is_paid   true → green, false → red (payment cell + Realizirano date)
  const statusTokens = theme === 'dark' ? darkthemecolors.token : customStyle.token;
  const statusBg = (flag) =>
    flag === true ? statusTokens.colorSuccessBg
    : flag === false ? statusTokens.colorDangerBg
    : undefined;
  const issuedTooltip = (flag) => flag ? 'Račun evidentiran' : 'Račun še ni evidentiran';
  const paidTooltip = (flag) => flag ? 'Plačano' : 'Ni plačano';

  const tagsById = useSelector((state) => state.Tags.byId);
  const companiesById = useSelector((state) => state.Companies.byId);
  const personsById = useSelector((state) => state.Persons.byId);
  const projectsById = useSelector((state) => state.Projects.byId);
  const accountsById = useSelector((state) => state.Account.byId);

  const [flagModalIntent, setFlagModalIntent] = useState(null);
  const [flagModalText, setFlagModalText] = useState('');
  const [flagSaving, setFlagSaving] = useState(false);

  // Optimistic rows: a just-toggled flag or a just-saved cell shows instantly
  // while the server-side reload (props.onIntentChanged) is in flight. Reset
  // whenever fresh rows arrive, so the server stays the source of truth. Keyed
  // on the results array, not intentsData: Cashflow and the budget views wrap
  // theirs in a fresh {results} object on every render.
  const [rowOverrides, setRowOverrides] = useState({});
  const results = props.intentsData?.results;
  useEffect(() => { setRowOverrides({}); }, [results]);
  const overrideRow = (id, fields) =>
    setRowOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...fields } }));
  // Rows handed to the columns already carry their overrides.
  const isFlaggedOf = (r) => r.is_flagged;

  const closeFlagModal = () => {
    setFlagModalIntent(null);
    setFlagModalText('');
    setFlagSaving(false);
  };

  const handleFlagSave = () => {
    const text = flagModalText.trim();
    if (!text || !flagModalIntent) return;
    const id = flagModalIntent.id;
    setFlagSaving(true);
    api
      .create(`/api/intents/${id}/comments/`, { text }, { successMsg: null })
      .then(() =>
        api.update(`/api/intents/${id}/`, { is_flagged: true }, { successMsg: null })
      )
      .then(() => {
        overrideRow(id, { is_flagged: true });
        closeFlagModal();
        props.onIntentChanged?.();
      })
      .catch((err) => {
        console.error('Flag shortcut error:', err);
        setFlagSaving(false);
      });
  };

  const handleUnflag = (intent) => {
    api
      .update(`/api/intents/${intent.id}/`, { is_flagged: false }, { successMsg: null })
      .then(() => {
        overrideRow(intent.id, { is_flagged: false });
        props.onIntentChanged?.();
      })
      .catch((err) => console.error('Unflag error:', err));
  };

  const orgConfig = useOrgConfig();

  // --- inline editing ------------------------------------------------------
  // Double-click a cell to edit it in place. The save is exactly the drawer's
  // (intentSave.js): same payload, same endpoint, and the same Split / Wizard
  // / BudgetSync confirmation in front of it, in the modes the drawer opens in.
  const [editing, setEditing] = useState(null);          // {id, field}
  const [savingCell, setSavingCell] = useState(null);    // {id, field}
  const [pendingConfirm, setPendingConfirm] = useState(null); // {step, onOk, onCancel}
  const isCell = (c, id, field) => c?.id === id && c?.field === field;
  const closeCell = (id, field) => setEditing(cur => (isCell(cur, id, field) ? null : cur));

  // One save at a time: each one reruns the org-wide DDV recompute server-side.
  const saveChain = useRef(Promise.resolve());
  const enqueue = (fn) => {
    const run = saveChain.current.then(fn);
    saveChain.current = run.catch(() => {});
    return run;
  };

  const commitInline = (row, field, value) => {
    const values = inlineFormValues(row, field, value, orgConfig);
    const dimension = defaultEditDimension(row);
    const cell = { id: row.id, field };

    const save = (choices = {}) => {
      setSavingCell(cell);
      return enqueue(() => {
        const payload = buildSavePayload(values, { orgConfig, intentData: row, isEdit: true, ...choices });
        const { request } = submitIntentSave(payload, {
          id: row.id, intentData: row, dimension,
          splitEditMode: 'master', wizardEditMode: 'all',
          includePastUnpaid: false, includePastPaid: false,
          quiet: true,
        });
        return request.then(resp => {
          // The API client resolves a 403 as null instead of rejecting.
          if (!resp) throw new Error('Inline save refused');
          overrideRow(row.id, resp.id === row.id ? resp : { [field]: payload[field] });
          closeCell(row.id, field);
          props.onIntentChanged?.();
        });
      })
        .catch(err => console.error('Inline save error:', err))
        .finally(() => setSavingCell(cur => (isCell(cur, row.id, field) ? null : cur)));
    };
    const confirm = (step, onOk) =>
      setPendingConfirm({ step, onOk, onCancel: () => closeCell(row.id, field) });

    // The drawer's onSubmit order, with its default modes: a split row saves
    // the whole group through its master, a repeating row every occurrence.
    const step = confirmStepFor(row, dimension);
    if (step === 'split') {
      confirm(step, () => save({ wizardChoice: null, budgetSyncMode: null, splitChoice: 'all' }));
    } else if (step === 'wizard') {
      confirm(step, () => save({ wizardChoice: 'all' }));
    } else if (step === 'budget') {
      setSavingCell(cell);
      checkBudgetSync(row.id, values)
        .then(resp => {
          if (resp?.needs_dialog) {
            setSavingCell(null);
            confirm(step, (syncMode) => save({ wizardChoice: null, budgetSyncMode: syncMode }));
          } else {
            save();
          }
        })
        .catch(err => { console.error('Budget sync check error:', err); setSavingCell(null); });
    } else {
      save();
    }
  };

  const startInlineEdit = (row, field, kind) => {
    if (kind === 'drawer') { props.onIntentEdit?.(row); return; }
    if (isCell(savingCell, row.id, field)) return;
    if (kind === 'toggle') { commitInline(row, field, !row[field]); return; }
    setEditing({ id: row.id, field });
  };

  const resolvePendingConfirm = (ok, arg) => {
    const pending = pendingConfirm;
    setPendingConfirm(null);
    if (ok) pending?.onOk(arg);
    else pending?.onCancel();
  };

  const { prefs: columnPrefs, moveColumn, toggleColumn } = useIntentsColumnPrefs();
  // Local per-instance: only one table is being edited at a time, while the
  // prefs themselves are global, so an edit here reorders every mounted table.
  // Controlled when the parent hosts IntentsColumnConfig itself; otherwise this
  // table owns the state and renders the control above itself, as before.
  const [ownEditMode, setOwnEditMode] = useState(false);
  const columnEditIsControlled = props.columnEditMode !== undefined;
  const editMode = columnEditIsControlled ? props.columnEditMode : ownEditMode;
  const setEditMode = columnEditIsControlled
    ? props.onColumnEditModeChange
    : setOwnEditMode;

  const declaredColumns = [
    // {title: '', dataIndex: 'id', render: id => <Text>#{id}</Text>},
    {title: 'Tip', dataIndex: 'type', fixed: 'left', render: (type, record) => (
      <>
        <Tooltip title={type}>{intentTypeIcons[type]}</Tooltip>
        {record.split_percent != null && (
          <Tooltip title="Transakcija razdeljena med projekte">
            <BranchesOutlined style={{ marginLeft: 6, color: '#1677ff' }} />
          </Tooltip>
        )}
        {record.wizard_type == 'PERIODIC' ? <span style={{ marginLeft: "6px"}}>{repeatableTooltipedIcon}</span> : null}
      </>
    )},
    {title: 'Status', key: 'status', fixed: 'left', render: (_, r) => (
      <Space size={4}>
        {r.warning && <WarnTriangle tooltip={r.warning}/>}
        {r.type === 'TAX' && r.manually_overridden_fields?.includes('amount') && (
          <Tooltip title="Davčni znesek je ročno nastavljen (zaklenjen).">
            <EditOutlined style={{ color: '#1677ff' }} />
          </Tooltip>
        )}
        {isFlaggedOf(r) && (
          <Tooltip title="Označeno – potrebuje pozornost">
            <FlagFilled style={{color:'#ff4d4f'}}/>
          </Tooltip>
        )}
      </Space>
    )},
    {title: 'Naziv', dataIndex: 'title', fixed: 'left', width: 240, ellipsis: { showTitle: false }, render: (title, intent) => (
      <Tooltip title={title}>
        <Text>
          {title}
          {intent.split_percent != null && (
            <Text type="secondary"> ({Number(intent.split_percent)}%)</Text>
          )}
          <OverrideIcon field="title" intent={intent} />
        </Text>
      </Tooltip>
    )},
    {title: 'Podjetje', dataIndex: 'company', width: 160, ellipsis: { showTitle: false }, render: cId => {
      return <Tooltip title={companiesById[cId]?.title}><Text>{companiesById[cId]?.title}</Text></Tooltip>
    }},
    {title: 'Oseba', dataIndex: 'person', width: 160, ellipsis: { showTitle: false }, render: pId => {
      return <Tooltip title={personsById[pId]?.title}><Text>{personsById[pId]?.title}</Text></Tooltip>
    }},
    {title: 'Projekt', dataIndex: 'project', width: 160, ellipsis: { showTitle: false }, render: projId => {
      return <Tooltip title={projectsById[projId]?.title}><Text>{projectsById[projId]?.title}</Text></Tooltip>
    }},
    {title: 'Račun', dataIndex: 'account', width: 160, ellipsis: { showTitle: false }, render: accId => {
      return <Tooltip title={accountsById[accId]?.title}><Text>{accountsById[accId]?.title}</Text></Tooltip>
    }},
    {title: 'Plačano', dataIndex: 'is_paid',
      onCell: r => ({ style: { backgroundColor: statusBg(r.is_paid) } }),
      render: x => <Tooltip title={paidTooltip(x)}><span><BoolDisplay val={x} /></span></Tooltip>},
    {title: 'Izdano / Prejeto', dataIndex: 'is_issued', requires: c => c.track_issuance,
      onCell: r => ({ style: { backgroundColor: statusBg(r.is_issued) } }),
      render: x => <Tooltip title={issuedTooltip(x)}><span><BoolDisplay val={x} /></span></Tooltip>},
    {title: 'Prejeto', dataIndex: 'date_invoice_recieved', requires: c => !c.single_date,
      onCell: r => r.type === 'EXPENSE' ? { style: { backgroundColor: statusBg(r.is_issued) } } : {},
      render: (dt, intent) => {
        const content = <Text>{utils.formatDt(dt)}<OverrideIcon field="date_invoice_recieved" intent={intent} /></Text>;
        return intent.type === 'EXPENSE' ? <Tooltip title={issuedTooltip(intent.is_issued)}>{content}</Tooltip> : content;
      }},
    {title: 'Opravljeno', dataIndex: 'date_service_performed', requires: c => !c.single_date,
      onCell: r => r.type === 'INCOME' ? { style: { backgroundColor: statusBg(r.is_issued) } } : {},
      render: (dt, intent) => {
        const content = <Text>{utils.formatDt(dt)}<OverrideIcon field="date_service_performed" intent={intent} /></Text>;
        return intent.type === 'INCOME' ? <Tooltip title={issuedTooltip(intent.is_issued)}>{content}</Tooltip> : content;
      }},
    {title: orgConfig.single_date ? 'Datum' : 'Realizirano', dataIndex: 'date_to_be_paid',
      onCell: r => ({ style: { backgroundColor: statusBg(r.is_paid) } }),
      render: (dt, intent) => <Tooltip title={paidTooltip(intent.is_paid)}><Text>{utils.formatDt(dt)}<OverrideIcon field="date_to_be_paid" intent={intent} /></Text></Tooltip>},
    {title: 'Znesek brez DDV', dataIndex: 'amount_no_tax', requires: c => c.tax_enabled, render: (amount_no_tax, intent) => <><MoneyDisplay type={intent.type} val={amount_no_tax} /><OverrideIcon field="amount_no_tax" intent={intent} /></>},
    {title: orgConfig.tax_enabled ? 'Znesek z DDV' : 'Znesek', dataIndex: 'amount', render: (amount, intent) => <><MoneyDisplay type={intent.type} val={amount} /><OverrideIcon field="amount" intent={intent} /></>},
    {title: 'Davčna stopnja', dataIndex: 'tax_rate', requires: c => c.tax_enabled, render: (tam, intent) => <Text><small>{tam} %</small><OverrideIcon field="tax_rate" intent={intent} /></Text>},
    {title: 'Oznake', dataIndex: 'tag', render: tId => {
      // Show the whole ancestry, not just the leaf: sibling subtrees reuse the
      // same leaf title (e.g. two different "banka" tags), so the leaf alone is
      // ambiguous. Ancestors are dimmed so the actual tag still reads first.
      const chain = utils.tagAncestryTitles(tagsById, tId);
      if (!chain.length) return null;
      const leaf = chain[chain.length - 1];
      const ancestors = chain.slice(0, -1);
      return (
        <Tooltip title={chain.join(' › ')}>
          <Tag color={tagsById[tId]?.color_hex} style={{ whiteSpace: 'normal' }}>
            {ancestors.map((a, i) => (
              <span key={i} style={{ opacity: 0.6 }}>{a} › </span>
            ))}
            {leaf}
          </Tag>
        </Tooltip>
      );
    }},
    {title: 'Uredi', key: 'edit', fixed: 'right', render: r => <Button size='small' onClick={ev => props.onIntentEdit(r)}><EditFilled/></Button>},
    {title: 'Označi', key: 'flag', fixed: 'right', render: r => (
      isFlaggedOf(r) ? (
        <Popconfirm
          title="Odznači to transakcijo?"
          okText="Odznači"
          cancelText="Prekliči"
          onConfirm={() => handleUnflag(r)}
        >
          <Tooltip title="Odznači">
            <Button size='small' danger><FlagFilled style={{color:'#ff4d4f'}}/></Button>
          </Tooltip>
        </Popconfirm>
      ) : (
        <Tooltip title="Označi in dodaj komentar">
          <Button size='small' onClick={() => setFlagModalIntent(r)}><FlagOutlined/></Button>
        </Tooltip>
      )
    )}
  ]

  const keyOf = c => c.key ?? c.dataIndex

  // Makes a data column's cells double-click editable. Key-only columns
  // (status, edit, flag) carry no field and pass through untouched.
  const withInlineEdit = (col) => {
    const field = col.dataIndex
    if (props.inlineEdit === false || !field) return col
    return {
      ...col,
      // Compose rather than replace: is_paid and the date columns already use
      // onCell for their status background.
      onCell: (record, inx) => {
        const base = col.onCell ? col.onCell(record, inx) : {}
        const kind = inlineEditKind(record, field, orgConfig)
        if (!kind || isCell(editing, record.id, field)) return base
        return {
          ...base,
          className: [base.className, 'inline-editable'].filter(Boolean).join(' '),
          // A double-click would otherwise also select the word under it.
          onMouseDown: e => { if (e.detail > 1) e.preventDefault() },
          onDoubleClick: () => startInlineEdit(record, field, kind),
        }
      },
      render: (value, record, inx) => {
        if (isCell(editing, record.id, field)) {
          return (
            <InlineEditCell
              kind={inlineEditKind(record, field, orgConfig)}
              value={value}
              min={record.type === 'TAX' ? -Infinity : 0}
              saving={isCell(savingCell, record.id, field)}
              onCommit={v => commitInline(record, field, v)}
              onCancel={() => closeCell(record.id, field)}
            />
          )
        }
        const content = col.render ? col.render(value, record, inx) : value
        if (isCell(savingCell, record.id, field)) {
          return <span style={{ opacity: 0.5 }}>{content} <LoadingOutlined /></span>
        }
        const kind = inlineEditKind(record, field, orgConfig)
        if (!kind) return content
        // The pencil follows the value, shown on hover (see .inline-edit-value);
        // a click does what a double-click on the cell does.
        return (
          <span className="inline-edit-value">
            <span className="inline-edit-value__text">{content}</span>
            <EditOutlined
              className="inline-edit-hint"
              onClick={e => { e.stopPropagation(); startInlineEdit(record, field, kind) }}
            />
          </span>
        )
      },
    }
  }

  // Wraps a column's header with the reorder/visibility controls. Applied last,
  // so it cannot disturb the resolved order or the `fixed` sanitation.
  const decorateForEdit = (col, i, arr) => {
    const key = keyOf(col)
    const ghost = hidden.has(key)
    const locked = utils.INTENTS_LOCKED_COLUMNS.includes(key)
    return {
      ...col,
      title: (
        <div style={{ opacity: ghost ? 0.45 : 1 }}>
          <div>{col.title}</div>
          <Space size={0}>
            <Button size="small" type="text" disabled={i === 0}
                    icon={<LeftOutlined />}
                    onClick={() => moveColumn(order, hidden, key, -1)} />
            <Tooltip title={locked
                ? 'Tega stolpca ni mogoče skriti'
                : ghost ? 'Prikaži stolpec' : 'Skrij stolpec'}>
              {/* span: antd Tooltip needs a non-disabled child to fire on */}
              <span>
                <Button size="small" type="text" disabled={locked}
                        icon={ghost ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => toggleColumn(order, hidden, key)} />
              </span>
            </Tooltip>
            <Button size="small" type="text" disabled={i === arr.length - 1}
                    icon={<RightOutlined />}
                    onClick={() => moveColumn(order, hidden, key, 1)} />
          </Space>
        </div>
      ),
      // Ghost body cells stay rendered but dimmed, so the user can see what the
      // column would give back. Compose rather than replace: is_paid and the date
      // columns already use onCell for their status background.
      onCell: ghost
        ? (record, inx) => {
            const base = col.onCell ? col.onCell(record, inx) : {}
            return { ...base, style: { ...(base.style || {}), opacity: 0.3 } }
          }
        : col.onCell,
    }
  }

  // 1. `requires`: the column has no data in this mode at all. It is never
  //    listed, ordered, or user-enableable.
  const availableColumns = declaredColumns.filter(c => !c.requires || c.requires(orgConfig))

  // 2. org default order/hidden, overridden by this user's saved layout.
  const { order, hidden } = utils.resolveColumnLayout(
    availableColumns.map(keyOf), orgConfig, columnPrefs
  )

  // 3. Outside edit mode a hidden column is gone; inside it stays as a ghost so
  //    it can be switched back on.
  const keptColumns = editMode
    ? availableColumns
    : availableColumns.filter(c => !hidden.has(keyOf(c)))

  // 4. Order last, so `fixed` sanitation sees the final arrangement.
  const orderedColumns = utils.orderColumns(keptColumns, order)

  const columns = editMode
    ? orderedColumns.map((col, i) => decorateForEdit(col, i, orderedColumns))
    : orderedColumns.map(withInlineEdit)

  // Selection is controlled only where the page owns it (props.selectedIds);
  // Cashflow and the project views leave it to the table. With
  // setAllMatchingSelected wired, the header checkbox selects every row the
  // query matches, not just this page -- see useIntentSelection.
  const rows = (results || []).map(r => (rowOverrides[r.id] ? { ...r, ...rowOverrides[r.id] } : r))
  const pageIds = rows.map(r => r.id)
  const total = props.intentsData?.pagination?.count ?? pageIds.length
  const selectionControlled = Array.isArray(props.selectedIds)
  const selectedKeys = props.allMatchingSelected ? pageIds : props.selectedIds
  const wholePageSelected =
    pageIds.length > 0 && pageIds.every(id => selectedKeys?.includes(id))
  const showSelectionBanner =
    !!props.setAllMatchingSelected && wholePageSelected && total > pageIds.length

  return (
    <>
      {!props.hideColumnConfig && !columnEditIsControlled && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <IntentsColumnConfig editMode={editMode} setEditMode={setEditMode} />
        </div>
      )}

      {showSelectionBanner && (
        <Alert
          type="info"
          style={{ marginBottom: 8 }}
          message={props.allMatchingSelected ? (
            <>
              {`Izbranih je vseh ${total} transakcij, ki ustrezajo filtrom (vse strani). `}
              <Link onClick={() => {
                props.setAllMatchingSelected(false)
                props.setSelectedIds(pageIds)
              }}>
                Izberi samo to stran
              </Link>
            </>
          ) : (
            <>
              {`Izbranih je ${props.selectedIds.length} transakcij. `}
              <Link onClick={() => props.setAllMatchingSelected(true)}>
                {`Izberi vseh ${total}, ki ustrezajo filtrom`}
              </Link>
            </>
          )}
        />
      )}

      <Table
        size="small"
        scroll={{ x: 'max-content' }}
        loading={props.loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        rowKey={intent => intent.id}
        rowSelection={{
          fixed: true,
          ...(selectionControlled && {
            selectedRowKeys: selectedKeys,
            // Rows picked on other pages stay picked (and counted).
            preserveSelectedRowKeys: true,
          }),
          // Fires before onChange, and only for the header checkbox.
          onSelectAll: (selected) => props.setAllMatchingSelected?.(selected),
          onChange: (selectedIds, _rows, info) => {
            // Cashflow and the project views render this table without
            // selection wiring; an unguarded call throws on row select.
            props.setSelectedIds?.(selectedIds)
            // Toggling a single row narrows "all matching" back to explicit ids.
            if (info?.type !== 'all') props.setAllMatchingSelected?.(false)
          }
        }}
        rowClassName={(record) => {
          if (record.warning){
            return theme === 'dark' ? 'row-warning-dark' : 'row-warning'
          }
        }}
      />
      {!props.hidePagination && (
        <Pagination
          current={props.page}
          onChange={pInx => props.onPageChange(pInx)}
          showSizeChanger={false}
          {...utils.processPagination(props.intentsData?.pagination)}
        />
      )}

      <Modal
        title="Označi transakcijo"
        open={!!flagModalIntent}
        onCancel={closeFlagModal}
        onOk={handleFlagSave}
        okText="Označi"
        cancelText="Prekliči"
        okButtonProps={{ disabled: !flagModalText.trim() || flagSaving, loading: flagSaving }}
        destroyOnClose
      >
        <p style={{ marginTop: 0 }}>Vpiši prvi komentar – pojasni, zakaj transakcija potrebuje pozornost.</p>
        <TextArea
          value={flagModalText}
          onChange={(e) => setFlagModalText(e.target.value)}
          autoFocus
          autoSize={{ minRows: 3, maxRows: 8 }}
          placeholder="Komentar…"
        />
      </Modal>

      <SplitUpdateConfirmModal
        open={pendingConfirm?.step === 'split'}
        mode="master"
        onOk={() => resolvePendingConfirm(true)}
        onCancel={() => resolvePendingConfirm(false)}
      />
      <WizardUpdateConfirmModal
        open={pendingConfirm?.step === 'wizard'}
        mode="all"
        onOk={() => resolvePendingConfirm(true)}
        onCancel={() => resolvePendingConfirm(false)}
      />
      <BudgetSyncModal
        open={pendingConfirm?.step === 'budget'}
        onOk={(syncMode) => resolvePendingConfirm(true, syncMode)}
        onCancel={() => resolvePendingConfirm(false)}
      />
    </>
  )
}

export default IntentsTable;
